// disable tslint for this file
// tslint:disable
import React, { useState, useCallback } from 'react';
import { IconButton, Link } from '@fluentui/react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';

import { useLogger } from '../Logger';
import { ShortHeadline, LongHeadline, Description } from './text-container';

export type AdPlacementCardPreviewProps = {
  shortHeadline?: string,
  longHeadline?: string,
  description?: string,
  displayUrl?: string,
  finalUrl?: string,
  imageSrc?: string,
  price?: number,
  priceCurrencySymbol?: string,
};

const styleOverrides = {
  adPlacementCardPreview: {
    width: 'min-content',
    height: 'auto',
    marginBottom: '10px',
  },
  previewContent: {
    border: 'none',
    boxShadow: 'none',
    width: '100%',
    height: 'auto',
  },
  adsLabel: {
    border: '1px solid #ddd',
    padding: '0 2px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
    borderRadius: 6,
    margin: '0 4px 0 0',
    display: 'inline',
    color: '#767676',
    opacity: '100%',
    position: 'static',
    maxHeight: 16,
  },
  cardBackgroud: {
    width: '520px',
    height: 'auto',
    background: 'radial-gradient(61.04% 89.69% at 100% 100%, rgb(211 241 249) 0%, rgba(28, 210, 229, 0.08) 40.63%, rgba(28, 210, 229, 0) 100%), radial-gradient(43.78% 64.34% at 60.31% 100%, rgb(23 228 118 / 8%) 0%, rgb(23 228 118 / 0%) 100%), linear-gradient(180deg, rgb(23 228 118 / 0%) 29.44%, rgb(23 228 118 / 6%) 100%), linear-gradient(90deg, #eaebf6 0%, #ecebf9 100%)',
    boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
    borderRadius: '4px',
    padding: '20px 20px 8px 20px',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '500px',
    height: 'auto',
  },
  imageContainer: {
    maxWidth: 120,
    maxHeight: 90,
    display: 'inline-block',
    verticalAlign: 'top',
    marginRight: 16,
  },
  textContainer: {
    width: '500px',
    margin: '4px 8px 0px 0px',
  },
  footerConatiner: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 6,
    '& .feed-back': {
      marginLeft: 'calc(100% - 70px)',
    },
  },
  footerRate: {
    display: 'flex',
    flexDirection: 'row',
    alighItems: 'start',
    '& .half-star': {
      marginLeft: 4,
      alignItems: 'center',
      fontWeight: '800',
      color: '#ffb900',
    },
    '& .views': {
      marginLeft: 8,
    },
  },
  footerPrice: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    '& .delivery': {
      marginLeft: 8,
    },
  },
  longHeadline: {
    fontWeight: '400',
    fontSize: '20px',
    lineHeight: '24px',
    height: '28px',
    width: '80%',
    overflow: 'hidden',
    color: '#0078d4',
  },
  finalUrl: {
    overflow: 'hidden',
    color: '#0b6a0b',
    fontSize: '14px',
    height: '20px',
    width: '80%',
  },
  description: {
    color: '#333',
    display: 'inline-block',
    fontSize: '13px',
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  descriptionParent: {
    display: 'flex',
  },
};

const useStyles = createUseStyles({
  '@global': {
    'html, body': {
      height: '100%',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
    },
  },
  ...styleOverrides,
});

export const TextAdPlacementCardPreview: React.FunctionComponent<AdPlacementCardPreviewProps> = ({
  shortHeadline,
  longHeadline,
  description,
  displayUrl,
  finalUrl,
  imageSrc,
  price,
  priceCurrencySymbol,
}) => {
  const classes = useStyles();

  const showShortHeadline = !!shortHeadline;
  const showLongHeadline = !!longHeadline;
  const showDescription = !!description;
  // const showUrl = !!displayUrl && !!finalUrl;
  const showAdsLabelInlineWithDescription = true;
  const showImage = !!imageSrc;
  const showPrice = !!price && !!priceCurrencySymbol;
  const showViews = false;
  const showLike = !!displayUrl && !!finalUrl;

  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const logger = useLogger();

  const onLike = useCallback(() => {
    if (liked) {
      logger.logInfo('Like button cancelled');
    } else {
      logger.logInfo('Like button clicked');
      if (disliked) logger.logInfo('Dislike button cancelled');
    }
    setLiked(!liked);
    setDisliked(false);
  }, [liked, disliked, logger]);

  const onDislike = useCallback(() => {
    if (disliked) {
      logger.logInfo('Dislike button cancelled');
    } else {
      logger.logInfo('Dislike button clicked');
      if (liked) logger.logInfo('Like button cancelled');
    }
    setDisliked(!disliked);
    setLiked(false);
  }, [liked, disliked, logger]);

  const likeButtonStyle = {
    color: liked ? 'red' : 'blue',
  };
  const disLikeButtonStyle = {
    color: disliked ? 'red' : 'blue',
  };

  const rate = 'X';
  const viewer = 'X';

  return (
    <div className={classnames('ad-placement-card-preview', classes.adPlacementCardPreview)}>
      <div
        className={classnames('responsive-ad-preview-content', classes.previewContent)}
      >
        <div className={classes.cardBackgroud}>
          <Link
            className={classes.contentContainer}
            target="_blank"
            rel="noopener noreferrer"
            href={finalUrl}
          >
            {
              showImage
              && (
                <img
                  src={imageSrc}
                  alt="Ad"
                  className={classes.imageContainer}
                />
              )
            }
            <div className={classnames('text-container-apl', classes.textContainer)}>
              {
                showShortHeadline
                && (
                  <ShortHeadline
                    styleOverrides={styleOverrides}
                    shortHeadline={shortHeadline}
                  />
                )
              }
              {
                showLongHeadline
                && (
                  <LongHeadline
                    longHeadline={longHeadline}
                    styleOverrides={styleOverrides}
                  />
                )
              }
              {/* {
                showUrl &&
                (
                  <FinalUrl
                    styleOverrides={styleOverrides}
                    finalUrl={finalUrl}
                    displayUrl={displayUrl}
                  />
                )
              } */}
              {
                showDescription
                && (
                  <Description
                    showAdsLabel={showAdsLabelInlineWithDescription}
                    adsLabelText="Ad"
                    styleOverrides={styleOverrides}
                    description={description}
                  />
                )
              }
            </div>
          </Link>
          <div className={classes.footerConatiner}>
            {
              showViews && (
              <div className={classes.footerRate}>
                <div>
                  {`${rate} / 5`}
                  {' '}
                </div>
                <div className="half-star">★</div>
                <div className="views">{`(${viewer} views)`}</div>
              </div>
              )
            }
            {
              showPrice && (
              <div className={classes.footerPrice}>
                <div>
                  {`Price: ${priceCurrencySymbol}${price}`}
                </div>
                <div className="delivery">
                  Free Delivery
                </div>
              </div>
              )
            }
            {
              showLike && (
              <div className="feed-back">
                <IconButton style={likeButtonStyle} iconProps={{ iconName: 'Like' }} title="Like" ariaLabel="Like" checked={liked} onClick={onLike} />
                <IconButton style={disLikeButtonStyle} iconProps={{ iconName: 'Dislike' }} title="Dislike" ariaLabel="Dislike" checked={disliked} onClick={onDislike} />
              </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};
